import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { AlertCircle, ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";

import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchAdminOrders } from "@/services/admin/orders.api";
import type { AdminOrderListItem, AdminOrdersListParams } from "@/services/admin/orders.types";

import {
  clearOrderFilters,
  dateInputToFromUtc,
  dateInputToToUtc,
  DEFAULT_ORDERS_PAGE_SIZE,
  hasActiveOrderFilters,
  ORDER_STATUSES,
  parseOrdersSearchParams,
  PAYMENT_METHODS,
  PAYMENT_PROVIDERS,
  PAYMENT_STATUSES,
  serializeOrdersSearchParams,
  utcExclusiveToToDateInput,
  utcIsoToDateInput,
} from "./orderQueryParams";
import {
  formatCustomerDisplay,
  formatOrderCurrency,
  formatOrderDateTime,
  formatStudentDisplay,
  getOrderStatusLabel,
  getOrderStatusVariant,
  getPaymentMethodLabel,
  getPaymentProviderLabel,
  getPaymentStatusLabel,
  getPaymentStatusVariant,
  isEmphasizedOrderStatus,
  shortOrderId,
} from "./orderLabels";

const ALL_FILTER_VALUE = "__all__";

const OrdersTableSkeleton: React.FC = () => (
  <div className="hidden md:block space-y-2" aria-busy="true" aria-label="Carregando pedidos">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="h-12 rounded-md bg-gray-100 animate-pulse" />
    ))}
  </div>
);

const OrdersCardsSkeleton: React.FC = () => (
  <div className="md:hidden space-y-3" aria-busy="true" aria-label="Carregando pedidos">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="h-36 rounded-lg bg-gray-100 animate-pulse" />
    ))}
  </div>
);

interface OrderRowProps {
  order: AdminOrderListItem;
  ledgerSearch: string;
}

const OrderTableRow: React.FC<OrderRowProps> = ({ order, ledgerSearch }) => {
  const emphasized = isEmphasizedOrderStatus(order.orderStatus);

  return (
    <tr
      className={`border-b border-gray-100 ${emphasized ? "bg-amber-50/70" : "bg-white"}`}
    >
      <td className="px-3 py-3 text-sm whitespace-nowrap">
        {formatOrderDateTime(order.createdAtUtc)}
      </td>
      <td className="px-3 py-3 text-sm font-mono" title={order.id}>
        {shortOrderId(order.id)}
      </td>
      <td className="px-3 py-3 text-sm">
        <div>{formatCustomerDisplay(order.customerName, order.customerEmail)}</div>
        <div className="text-xs text-gray-500">
          {formatStudentDisplay(order.studentName, order.studentId)}
        </div>
      </td>
      <td className="px-3 py-3 text-sm">{order.packageName ?? "—"}</td>
      <td className="px-3 py-3 text-sm">
        <Badge variant={getOrderStatusVariant(order.orderStatus)}>
          {getOrderStatusLabel(order.orderStatus)}
        </Badge>
      </td>
      <td className="px-3 py-3 text-sm">
        <Badge variant={getPaymentStatusVariant(order.paymentStatus)}>
          {getPaymentStatusLabel(order.paymentStatus)}
        </Badge>
      </td>
      <td className="px-3 py-3 text-sm">
        <div>{getPaymentProviderLabel(order.paymentProvider)}</div>
        <div className="text-xs text-gray-500">
          {getPaymentMethodLabel(order.paymentMethod)}
        </div>
      </td>
      <td className="px-3 py-3 text-sm font-medium whitespace-nowrap">
        {formatOrderCurrency(order.total, order.currency)}
      </td>
      <td className="px-3 py-3 text-sm">
        <Link
          to={`/admin/orders/${order.id}`}
          state={{ ledgerSearch }}
          className="text-blue-700 underline underline-offset-2 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded-sm"
        >
          Ver detalhes
        </Link>
      </td>
    </tr>
  );
};

const OrderCard: React.FC<OrderRowProps> = ({ order, ledgerSearch }) => {
  const emphasized = isEmphasizedOrderStatus(order.orderStatus);

  return (
    <article
      className={`rounded-lg border p-4 space-y-3 ${
        emphasized ? "border-amber-300 bg-amber-50/70" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-gray-500">{formatOrderDateTime(order.createdAtUtc)}</p>
          <p className="font-mono text-sm" title={order.id}>
            {shortOrderId(order.id)}
          </p>
        </div>
        <p className="font-semibold">{formatOrderCurrency(order.total, order.currency)}</p>
      </div>

      <div className="text-sm space-y-1">
        <p>{formatCustomerDisplay(order.customerName, order.customerEmail)}</p>
        <p className="text-gray-600">{order.packageName ?? "Pacote indisponível"}</p>
        <p className="text-xs text-gray-500">
          {formatStudentDisplay(order.studentName, order.studentId)}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant={getOrderStatusVariant(order.orderStatus)}>
          Pedido: {getOrderStatusLabel(order.orderStatus)}
        </Badge>
        <Badge variant={getPaymentStatusVariant(order.paymentStatus)}>
          Pagamento: {getPaymentStatusLabel(order.paymentStatus)}
        </Badge>
      </div>

      <div className="text-xs text-gray-600">
        {getPaymentProviderLabel(order.paymentProvider)} ·{" "}
        {getPaymentMethodLabel(order.paymentMethod)}
      </div>

      <Link
        to={`/admin/orders/${order.id}`}
        state={{ ledgerSearch }}
        className="inline-flex text-sm text-blue-700 underline underline-offset-2 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded-sm"
      >
        Ver detalhes
      </Link>
    </article>
  );
};

const AdminOrdersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseOrdersSearchParams(searchParams), [searchParams]);

  const [searchInput, setSearchInput] = useState(query.search ?? "");
  const [debouncedSearch] = useDebounce(searchInput, 400);

  const [items, setItems] = useState<AdminOrderListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const isFirstLoadRef = useRef(true);

  const fromDateInput = utcIsoToDateInput(query.from);
  const toDateInput = utcExclusiveToToDateInput(query.to);

  const ledgerSearch = searchParams.toString();

  const updateQuery = useCallback(
    (patch: Partial<AdminOrdersListParams>, resetPage = false) => {
      const next: AdminOrdersListParams = {
        ...query,
        ...patch,
        page: resetPage ? 1 : patch.page ?? query.page ?? 1,
      };
      setSearchParams(serializeOrdersSearchParams(next), { replace: false });
    },
    [query, setSearchParams]
  );

  useEffect(() => {
    setSearchInput(query.search ?? "");
  }, [query.search]);

  useEffect(() => {
    const currentSearch = query.search ?? "";
    if (debouncedSearch.trim() === currentSearch.trim()) return;
    updateQuery({ search: debouncedSearch.trim() || undefined }, true);
  }, [debouncedSearch, query.search, updateQuery]);

  useEffect(() => {
    let cancelled = false;
    const isFirstLoad = isFirstLoadRef.current;

    if (isFirstLoad) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    const load = async () => {
      try {
        const page = await fetchAdminOrders(query);
        if (cancelled) return;
        setItems(page.items);
        setTotal(page.total);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load admin orders:", err);
        setError("Não foi possível carregar os pedidos.");
      }

      if (cancelled) return;

      if (isFirstLoad) {
        isFirstLoadRef.current = false;
        setInitialLoading(false);
      }
      setRefreshing(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [query, retryToken]);

  const totalPages = Math.max(1, Math.ceil(total / (query.pageSize ?? DEFAULT_ORDERS_PAGE_SIZE)));
  const hasFilters = hasActiveOrderFilters(query);
  const showGlobalEmpty = !initialLoading && !error && total === 0 && !hasFilters;
  const showFilteredEmpty = !initialLoading && !error && total === 0 && hasFilters;

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchParams(serializeOrdersSearchParams(clearOrderFilters(query)), { replace: false });
  };

  const handleRetry = () => setRetryToken((value) => value + 1);

  return (
    <AdminPageLayout title="Pedidos">
      <main className="py-6 space-y-6" aria-live="polite">
        <div>
          <h1 className="text-2xl font-bold">Pedidos</h1>
          <p className="text-gray-600">
            Console de suporte para consulta de pedidos, pagamentos e acesso.
          </p>
        </div>

        <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="relative md:col-span-2 xl:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                aria-label="Buscar pedidos"
                placeholder="ID, e-mail, nome, estudante ou pacote"
                className="pl-9"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>

            <Select
              value={query.orderStatus ?? ALL_FILTER_VALUE}
              onValueChange={(value) =>
                updateQuery(
                  { orderStatus: value === ALL_FILTER_VALUE ? undefined : (value as AdminOrdersListParams["orderStatus"]) },
                  true
                )
              }
            >
              <SelectTrigger aria-label="Filtrar status do pedido">
                <SelectValue placeholder="Status do pedido" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>Todos os status</SelectItem>
                {ORDER_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {getOrderStatusLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={query.paymentStatus ?? ALL_FILTER_VALUE}
              onValueChange={(value) =>
                updateQuery(
                  {
                    paymentStatus:
                      value === ALL_FILTER_VALUE
                        ? undefined
                        : (value as AdminOrdersListParams["paymentStatus"]),
                  },
                  true
                )
              }
            >
              <SelectTrigger aria-label="Filtrar status do pagamento">
                <SelectValue placeholder="Status do pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>Todos os pagamentos</SelectItem>
                {PAYMENT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {getPaymentStatusLabel(status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={query.provider ?? ALL_FILTER_VALUE}
              onValueChange={(value) =>
                updateQuery(
                  {
                    provider:
                      value === ALL_FILTER_VALUE
                        ? undefined
                        : (value as AdminOrdersListParams["provider"]),
                  },
                  true
                )
              }
            >
              <SelectTrigger aria-label="Filtrar provedor">
                <SelectValue placeholder="Provedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>Todos os provedores</SelectItem>
                {PAYMENT_PROVIDERS.map((provider) => (
                  <SelectItem key={provider} value={provider}>
                    {getPaymentProviderLabel(provider)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={query.method ?? ALL_FILTER_VALUE}
              onValueChange={(value) =>
                updateQuery(
                  {
                    method:
                      value === ALL_FILTER_VALUE
                        ? undefined
                        : (value as AdminOrdersListParams["method"]),
                  },
                  true
                )
              }
            >
              <SelectTrigger aria-label="Filtrar método">
                <SelectValue placeholder="Método" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FILTER_VALUE}>Todos os métodos</SelectItem>
                {PAYMENT_METHODS.map((method) => (
                  <SelectItem key={method} value={method}>
                    {getPaymentMethodLabel(method)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              aria-label="Filtrar por ID do pacote"
              placeholder="ID do pacote"
              value={query.packageId ?? ""}
              onChange={(event) =>
                updateQuery({ packageId: event.target.value || undefined }, true)
              }
            />

            <Input
              aria-label="Data inicial"
              type="date"
              value={fromDateInput}
              onChange={(event) =>
                updateQuery({ from: dateInputToFromUtc(event.target.value) }, true)
              }
            />

            <Input
              aria-label="Data final"
              type="date"
              value={toDateInput}
              onChange={(event) =>
                updateQuery({ to: dateInputToToUtc(event.target.value) }, true)
              }
            />

            <Select
              value={query.sort ?? "createdAt:desc"}
              onValueChange={(value) =>
                updateQuery({ sort: value as AdminOrdersListParams["sort"] }, true)
              }
            >
              <SelectTrigger aria-label="Ordenar pedidos">
                <SelectValue placeholder="Ordenação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt:desc">Mais recentes</SelectItem>
                <SelectItem value="createdAt:asc">Mais antigos</SelectItem>
                <SelectItem value="updatedAt:desc">Atualizados recentemente</SelectItem>
                <SelectItem value="total:desc">Maior valor</SelectItem>
                <SelectItem value="total:asc">Menor valor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {initialLoading ? "Carregando…" : `${total} pedido${total === 1 ? "" : "s"}`}
              {refreshing && !initialLoading ? " · atualizando…" : ""}
            </p>
            <div className="flex gap-2">
              {hasFilters && (
                <Button type="button" variant="outline" onClick={handleClearFilters}>
                  Limpar filtros
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleRetry}
                disabled={initialLoading || refreshing}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                Atualizar
              </Button>
            </div>
          </div>
        </section>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900"
          >
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-2">
              <p>{error}</p>
              <Button type="button" variant="outline" onClick={handleRetry}>
                Tentar novamente
              </Button>
            </div>
          </div>
        )}

        {initialLoading && (
          <>
            <OrdersTableSkeleton />
            <OrdersCardsSkeleton />
          </>
        )}

        {!initialLoading && !error && showGlobalEmpty && (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
            <h2 className="text-lg font-semibold">Nenhum pedido</h2>
            <p className="mt-2 text-gray-600">Ainda não há pedidos registrados no sistema.</p>
          </div>
        )}

        {!initialLoading && !error && showFilteredEmpty && (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
            <h2 className="text-lg font-semibold">Nenhum pedido encontrado</h2>
            <p className="mt-2 text-gray-600">
              Nenhum pedido corresponde aos filtros atuais.
            </p>
            <Button type="button" className="mt-4" variant="outline" onClick={handleClearFilters}>
              Limpar filtros
            </Button>
          </div>
        )}

        {!initialLoading && !error && items.length > 0 && (
          <>
            <div className={`hidden md:block overflow-x-auto rounded-lg border ${refreshing ? "opacity-60" : ""}`}>
              <table className="min-w-full text-left">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th scope="col" className="px-3 py-3">Criado</th>
                    <th scope="col" className="px-3 py-3">Pedido</th>
                    <th scope="col" className="px-3 py-3">Cliente / estudante</th>
                    <th scope="col" className="px-3 py-3">Pacote</th>
                    <th scope="col" className="px-3 py-3">Status pedido</th>
                    <th scope="col" className="px-3 py-3">Pagamento</th>
                    <th scope="col" className="px-3 py-3">Provedor / método</th>
                    <th scope="col" className="px-3 py-3">Total</th>
                    <th scope="col" className="px-3 py-3">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((order) => (
                    <OrderTableRow key={order.id} order={order} ledgerSearch={ledgerSearch} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className={`md:hidden space-y-3 ${refreshing ? "opacity-60" : ""}`}>
              {items.map((order) => (
                <OrderCard key={order.id} order={order} ledgerSearch={ledgerSearch} />
              ))}
            </div>

            <nav
              aria-label="Paginação de pedidos"
              className="flex items-center justify-between gap-3"
            >
              <Button
                type="button"
                variant="outline"
                disabled={(query.page ?? 1) <= 1 || refreshing}
                onClick={() => updateQuery({ page: Math.max(1, (query.page ?? 1) - 1) })}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Anterior
              </Button>
              <span className="text-sm text-gray-600">
                Página {query.page ?? 1} de {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                disabled={(query.page ?? 1) >= totalPages || refreshing}
                onClick={() => updateQuery({ page: (query.page ?? 1) + 1 })}
              >
                Próxima
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </nav>
          </>
        )}
      </main>
    </AdminPageLayout>
  );
};

export default AdminOrdersPage;
